import { DeleteOutlined, FileWordOutlined, SaveOutlined, SendOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Form, Input, Row, Select, Space, Table, Tag, Tooltip, Upload } from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { responseStatus, urlAdmin } from "../../constants";
import Summernote from "../../components/summernote/Summernote";
import { Links } from "../../helpers/Links";
import { dateCommon, validateMessages } from "../../helpers/common";
import { ChiTietThanhLyTaiSan, ThanhLyTaiSanRequest, TrangThaiThanhLyTaiSan } from "../../models/taisan/ThanhLyTaiSan";
import { TaiSan, TrangThaiTaiSan } from "../../models/taisan/TaiSan";
import { hoanThanhThanhLy, layDanhSachTaiSanCoTheThanhLy, luuPhieuThanhLy } from "../../services/thanhlytaisan";

const LuuThanhLyTaiSanBox = ({ modelProps, onSubmit }: {
    modelProps: ChiTietThanhLyTaiSan | null;
    onSubmit: (id: number, action: "draft" | "complete") => void;
}) => {
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [isLoading, setIsLoading] = useState(false);
    const [noiDung, setNoiDung] = useState("");
    const [tuKhoaTaiSan, setTuKhoaTaiSan] = useState("");
    const [filterTrangThaiTaiSan, setFilterTrangThaiTaiSan] = useState<number | undefined>();
    const [dsTaiSanKhaDung, setDsTaiSanKhaDung] = useState<TaiSan[]>([]);
    const [idsTaiSanChon, setIdsTaiSanChon] = useState<number[]>([]);
    const [idsTaiLieuXoa, setIdsTaiLieuXoa] = useState<number[]>([]);
    const [fileList, setFileList] = useState<UploadFile[]>([]);

        const isDaGhiGiam = false; // Đã loại bỏ trạng thái này
        const isHoanThanh = modelProps?.trangThai === TrangThaiThanhLyTaiSan.HoanThanh;
        const khoaDanhSachTaiSan = isHoanThanh;

    const getTrangThaiTaiSanTag = (trangThai: number) => {
        switch (trangThai) {
            case TrangThaiTaiSan.MoiNhap:
                return <Tag color="blue">Mới nhập</Tag>;
            case TrangThaiTaiSan.DangSuDung:
                return <Tag color="green">Đang sử dụng</Tag>;
            case TrangThaiTaiSan.BaoTri:
                return <Tag color="orange">Bảo trì</Tag>;
            case TrangThaiTaiSan.Hong:
                return <Tag color="red">Hỏng</Tag>;
            case TrangThaiTaiSan.ChoThanhLy:
                return <Tag color="purple">Chờ thanh lý</Tag>;
            case TrangThaiTaiSan.DieuChuyen:
                return <Tag color="cyan">Điều chuyển</Tag>;
            case TrangThaiTaiSan.DaThanhLy:
                return <Tag color="red">Đã thanh lý</Tag>;
            default:
                return <Tag>Không xác định</Tag>;
        }
    };

    const loadDanhSachTaiSan = async () => {
        try {
            setIsLoading(true);
            const result = await layDanhSachTaiSanCoTheThanhLy();
            setDsTaiSanKhaDung(result);
        } catch (error: any) {
            toast.error("Lỗi khi tải danh sách tài sản: " + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const setFormDetail = (value: ChiTietThanhLyTaiSan | null) => {
        form.setFieldsValue({
            ngayLap: value?.ngayLap ? dayjs(value.ngayLap) : dayjs(),
        });
        setNoiDung(value?.noiDung || "");
        setIdsTaiSanChon((value?.danhSachTaiSan || []).map((item) => item.id!).filter(Boolean));
        setIdsTaiLieuXoa([]);
        setFileList((value?.dsTaiLieu || []).map((item) => ({
            uid: `${item.idTaiLieu}tailieu`,
            name: item.tenTep,
            status: "done",
            url: urlAdmin + item.duongDan.replace(/\\/g, "/"),
        })));
    };

    useEffect(() => {
        loadDanhSachTaiSan();
    }, []);

    useEffect(() => {
        setFormDetail(modelProps);
    }, [modelProps]);

    const mapTaiSan = useMemo(() => {
        const data = new Map<number, TaiSan>();
        dsTaiSanKhaDung.forEach((item) => data.set(item.id!, item));
        (modelProps?.danhSachTaiSan || []).forEach((item) => data.set(item.id!, item));
        return data;
    }, [dsTaiSanKhaDung, modelProps]);

    const dsTaiSanChon = useMemo(() => idsTaiSanChon
        .map((id) => mapTaiSan.get(id))
        .filter(Boolean) as TaiSan[], [idsTaiSanChon, mapTaiSan]);

    const dsTaiSanHienThi = useMemo(() => dsTaiSanKhaDung.filter((item) => {
        if (filterTrangThaiTaiSan !== undefined && item.trangThai !== filterTrangThaiTaiSan) {
            return false;
        }

        if (!tuKhoaTaiSan.trim()) {
            return true;
        }

        const tuKhoa = tuKhoaTaiSan.trim().toLowerCase();
        return (item.ma || "").toLowerCase().includes(tuKhoa)
            || (item.tenTaiSan || "").toLowerCase().includes(tuKhoa)
            || (item.tenDonVi || "").toLowerCase().includes(tuKhoa)
            || (item.nguoiSuDung || "").toLowerCase().includes(tuKhoa);
    }), [dsTaiSanKhaDung, tuKhoaTaiSan, filterTrangThaiTaiSan]);

    const uploadProps: UploadProps = {
        onRemove: (file) => {
            if (file.uid.includes("tailieu")) {
                const idTaiLieu = parseInt(file.uid.split("tailieu")[0]);
                setIdsTaiLieuXoa((prev) => [...prev, idTaiLieu]);
            }

            setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
        },
        beforeUpload: (file) => {
            const maxSize = 20000000;
            if (file.size > maxSize) {
                alert("Tệp tin đính kèm không được vượt quá 20MB");
            } else {
                setFileList((prev) => [...prev, file]);
            }
            return false;
        },
        fileList,
        multiple: true,
    };

    const themTaiSan = (idTaiSan: number) => {
        if (idsTaiSanChon.includes(idTaiSan)) {
            toast.warning("Tài sản đã có trong danh sách thanh lý");
            return;
        }
        setIdsTaiSanChon((prev) => [...prev, idTaiSan]);
    };

    const xoaTaiSan = (idTaiSan: number) => {
        setIdsTaiSanChon((prev) => prev.filter((item) => item !== idTaiSan));
    };

    const taoBangTaiSanHtml = () => {
        if (!dsTaiSanChon.length) {
            toast.warning("Vui lòng chọn ít nhất một tài sản để chèn bảng");
            return;
        }

        const tongNguyenGia = dsTaiSanChon.reduce((sum, item) => sum + (item.nguyenGia ?? item.giaTriKhiNhap ?? 0), 0);
        const tongGiaTriConLai = dsTaiSanChon.reduce((sum, item) => sum + (item.giaTriConLai ?? 0), 0);
        const tongSoLuong = dsTaiSanChon.reduce((sum, item) => sum + (item.soLuong ?? 1), 0);

        const rows = dsTaiSanChon.map((item, index) => `
            <tr>
                <td style="border:1px solid #000;padding:6px;text-align:center;">${index + 1}</td>
                <td style="border:1px solid #000;padding:6px;">${item.tenTaiSan || ""}</td>
                <td style="border:1px solid #000;padding:6px;">${item.ma || ""}</td>
                <td style="border:1px solid #000;padding:6px;text-align:right;">${(item.nguyenGia ?? item.giaTriKhiNhap ?? 0).toLocaleString("vi-VN")}</td>
                <td style="border:1px solid #000;padding:6px;text-align:right;">${(item.giaTriConLai ?? 0).toLocaleString("vi-VN")}</td>
                <td style="border:1px solid #000;padding:6px;text-align:right;">${item.soLuong ?? 1}</td>
                <td style="border:1px solid #000;padding:6px;text-align:center;">${item.donViTinh || "Cái"}</td>
                <td style="border:1px solid #000;padding:6px;">${item.ghiChu || item.moTa || ""}</td>
            </tr>
        `).join("");

        const tableHtml = `
            <table style="width:100%;border-collapse:collapse;margin:8px 0;font-family:'Times New Roman', Times, serif;font-size:14px;">
                <thead>
                    <tr style="font-weight:bold;text-align:center;background-color:#f2f2f2;">
                        <th style="border:1px solid #000;padding:6px;">STT</th>
                        <th style="border:1px solid #000;padding:6px;">Tên công cụ dụng cụ</th>
                        <th style="border:1px solid #000;padding:6px;">Mã công cụ dụng cụ</th>
                        <th style="border:1px solid #000;padding:6px;">Nguyên giá (đồng)</th>
                        <th style="border:1px solid #000;padding:6px;">Giá trị còn lại</th>
                        <th style="border:1px solid #000;padding:6px;">Số lượng</th>
                        <th style="border:1px solid #000;padding:6px;">ĐVT</th>
                        <th style="border:1px solid #000;padding:6px;">Ghi chú</th>
                    </tr>
                    <tr style="font-weight:bold;background-color:#fafafa;">
                        <td colspan="3" style="border:1px solid #000;padding:6px;text-align:center;">Tổng cộng:</td>
                        <td style="border:1px solid #000;padding:6px;text-align:right;">${tongNguyenGia.toLocaleString("vi-VN")}</td>
                        <td style="border:1px solid #000;padding:6px;text-align:right;">${tongGiaTriConLai.toLocaleString("vi-VN")}</td>
                        <td style="border:1px solid #000;padding:6px;text-align:right;">${tongSoLuong}</td>
                        <td colspan="2" style="border:1px solid #000;padding:6px;"></td>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>
            <p><br/></p>
        `;
        setNoiDung(prev => (prev || "") + tableHtml);
    };

    const luuPhieu = async () => {
        const values = await form.validateFields();

        if (!idsTaiSanChon.length) {
            throw new Error("Vui lòng chọn ít nhất một tài sản thanh lý");
        }

        const request: ThanhLyTaiSanRequest = {
            id: modelProps?.id || undefined,
            ngayLap: values.ngayLap ? dayjs(values.ngayLap).format("YYYY-MM-DDTHH:mm:ss") : "",
            noiDung,
            idsTaiSan: idsTaiSanChon,
            idsTaiLieuThanhLyXoa: idsTaiLieuXoa,
            files: fileList,
        };

        const response = await luuPhieuThanhLy(request);
        if (response.trangThai !== responseStatus.thanhCong) {
            throw new Error(response.thongBao);
        }

        return response.duLieu;
    };

    const handleSubmit = async (action: "draft" | "complete") => {
        if (action === "complete" && fileList.length === 0) {
            toast.warning("Vui lòng đính kèm ít nhất 1 file trước khi hoàn thành thanh lý");
            return;
        }

        try {
            setIsLoading(true);
            const id = await luuPhieu();

            if (action === "complete") {
                const response = await hoanThanhThanhLy(id);
                if (response.trangThai !== responseStatus.thanhCong) {
                    throw new Error(response.thongBao);
                }
                toast.success("Hoàn thành thanh lý tài sản thành công");
            } else {
                toast.success("Lưu phiếu thanh lý thành công");
            }

            onSubmit(id, action);
        } catch (error: any) {
            toast.error(error.message || "Đã xảy ra lỗi khi lưu phiếu thanh lý");
        } finally {
            setIsLoading(false);
        }
    };

    const columnsTaiSanKhaDung = [
        {
            title: "Mã tài sản",
            key: "ma",
            width: 140,
            render: (_: any, record: TaiSan) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{record.ma}</div>
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>{record.tenTaiSan}</div>
                </div>
            ),
        },
        {
            title: "Loại tài sản",
            dataIndex: "loaiTaiSan",
            key: "loaiTaiSan",
            width: 180,
        },
        {
            title: "Bộ phận sử dụng",
            dataIndex: "tenDonVi",
            key: "tenDonVi",
            width: 220,
        },
        {
            title: "Người sử dụng",
            dataIndex: "nguoiSuDung",
            key: "nguoiSuDung",
            width: 180,
            render: (value: string) => value || "—",
        },
        {
            title: "Trạng thái",
            dataIndex: "trangThai",
            key: "trangThai",
            width: 140,
            render: (value: number) => getTrangThaiTaiSanTag(value),
        },
        {
            title: "",
            key: "action",
            width: 90,
            align: "center" as const,
            render: (_: any, record: TaiSan) => (
                <Button
                    type={idsTaiSanChon.includes(record.id!) ? "default" : "primary"}
                    size="small"
                    disabled={khoaDanhSachTaiSan || idsTaiSanChon.includes(record.id!)}
                    onClick={() => themTaiSan(record.id!)}
                >
                    {idsTaiSanChon.includes(record.id!) ? "Đã chọn" : "Thêm"}
                </Button>
            ),
        },
    ];

    const columnsTaiSanChon = [
        {
            title: "STT",
            key: "stt",
            width: 60,
            align: "center" as const,
            render: (_: any, __: any, index: number) => index + 1,
        },
        {
            title: "Tên công cụ dụng cụ",
            dataIndex: "tenTaiSan",
            key: "tenTaiSan",
            width: 220,
        },
        {
            title: "Mã công cụ dụng cụ",
            dataIndex: "ma",
            key: "ma",
            width: 140,
        },
        {
            title: "Nguyên giá (đồng)",
            key: "nguyenGia",
            width: 140,
            align: "right" as const,
            render: (_: any, record: TaiSan) => ((record.nguyenGia ?? record.giaTriKhiNhap ?? 0).toLocaleString("vi-VN")),
        },
        {
            title: "Giá trị còn lại",
            dataIndex: "giaTriConLai",
            key: "giaTriConLai",
            width: 140,
            align: "right" as const,
            render: (value: number) => (value ?? 0).toLocaleString("vi-VN"),
        },
        {
            title: "Số lượng",
            dataIndex: "soLuong",
            key: "soLuong",
            width: 90,
            align: "right" as const,
            render: (value: number) => value ?? 1,
        },
        {
            title: "ĐVT",
            dataIndex: "donViTinh",
            key: "donViTinh",
            width: 80,
            align: "center" as const,
            render: (value: string) => value || "Cái",
        },
        {
            title: "Ghi chú",
            key: "ghiChu",
            width: 180,
            render: (_: any, record: TaiSan) => record.ghiChu || record.moTa || "—",
        },
        {
            title: "",
            key: "xoa",
            width: 60,
            align: "center" as const,
            render: (_: any, record: TaiSan) => (
                <Tooltip title={khoaDanhSachTaiSan ? "Phiếu đã ghi giảm, không thể thay đổi danh sách tài sản" : "Bỏ tài sản"}>
                    <Button
                        type="link"
                        danger
                        disabled={khoaDanhSachTaiSan}
                        icon={<DeleteOutlined />}
                        onClick={() => xoaTaiSan(record.id!)}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <Form
            form={form}
            layout="vertical"
            validateMessages={validateMessages}
            autoComplete="off"
        >
            <Row gutter={16}>
                <Col xs={24} md={8}>
                    <Form.Item name="ngayLap" label="Ngày lập" rules={[{ required: true }]}>
                        <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} disabled={isHoanThanh} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={24}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <h3 style={{ margin: 0 }}>Nội dung phiếu thanh lý</h3>
                        <Space>
                            <Button icon={<FileWordOutlined />} onClick={taoBangTaiSanHtml} disabled={!idsTaiSanChon.length || isHoanThanh}>
                                Chèn bảng tài sản vào nội dung
                            </Button>
                        </Space>
                    </div>
                    {!isHoanThanh ? <Summernote
                        value={noiDung}
                        onChange={setNoiDung}
                        placeholder="Nhập nội dung phiếu thanh lý tài sản..."
                        height={350}
                    /> : noiDung ?
                        <div className="document">
                            <div dangerouslySetInnerHTML={{ __html: noiDung || '' }} />
                        </div> : <div style={{ fontStyle: "italic", color: "#888" }}>Chưa có nội dung phiếu thanh lý</div>
                    }
                </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 24 }}>
                <Col span={24}>
                    <Form.Item label="File đính kèm">
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />} disabled={isHoanThanh}>Thêm file đính kèm</Button>
                        </Upload>
                    </Form.Item>
                </Col>
            </Row>

            <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <h3 style={{ margin: 0 }}>Danh sách tài sản</h3>
                    <Space>
                        <Select
                            allowClear
                            placeholder="Lọc trạng thái"
                            style={{ width: 160 }}
                            value={filterTrangThaiTaiSan}
                            onChange={(value) => setFilterTrangThaiTaiSan(value)}
                            options={[
                                { value: TrangThaiTaiSan.MoiNhap, label: 'Mới nhập' },
                                { value: TrangThaiTaiSan.DangSuDung, label: 'Đang sử dụng' },
                                { value: TrangThaiTaiSan.BaoTri, label: 'Bảo trì' },
                                { value: TrangThaiTaiSan.Hong, label: 'Hỏng' },
                                { value: TrangThaiTaiSan.ChoThanhLy, label: 'Chờ thanh lý' },
                                { value: TrangThaiTaiSan.DieuChuyen, label: 'Điều chuyển' },
                            ]}
                        />
                        <Input
                            placeholder="Tìm tài sản..."
                            value={tuKhoaTaiSan}
                            onChange={(e) => setTuKhoaTaiSan(e.target.value)}
                            style={{ width: 260 }}
                            allowClear
                        />
                    </Space>
                </div>
                <Table
                    columns={columnsTaiSanKhaDung}
                    dataSource={dsTaiSanHienThi}
                    rowKey="id"
                    loading={isLoading}
                    size="small"
                    pagination={{ pageSize: 8, showSizeChanger: false }}
                    scroll={{ x: 1000 }}
                />
            </div>

            <div style={{ marginTop: 24 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <h3 style={{ margin: 0 }}>Danh sách tài sản đã chọn cần thanh lý</h3>
                    <Tag color="blue">{dsTaiSanChon.length} tài sản</Tag>
                </div>
                <Table
                    columns={columnsTaiSanChon}
                    dataSource={dsTaiSanChon}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    scroll={{ x: 1100 }}
                    locale={{ emptyText: "Chưa có tài sản nào được chọn" }}
                    summary={(pageData) => {
                        let totalNguyenGia = 0;
                        let totalGiaTriConLai = 0;
                        let totalSoLuong = 0;

                        pageData.forEach((item) => {
                            totalNguyenGia += (item.nguyenGia ?? item.giaTriKhiNhap ?? 0);
                            totalGiaTriConLai += (item.giaTriConLai ?? 0);
                            totalSoLuong += (item.soLuong ?? 1);
                        });

                        return (
                            <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                                <Table.Summary.Cell index={0} colSpan={3} align="center">
                                    Tổng cộng:
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    {totalNguyenGia.toLocaleString('vi-VN')}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right">
                                    {totalGiaTriConLai.toLocaleString('vi-VN')}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="right">
                                    {totalSoLuong}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4} colSpan={3} />
                            </Table.Summary.Row>
                        );
                    }}
                />
            </div>

            <Row style={{ marginTop: 24 }}>
                <Col xs={24}>
                    <Space wrap>
                        <Button onClick={() => navigate(Links.ThanhLyTaiSan.DanhSach)}>
                            Hủy
                        </Button>
                        {!isHoanThanh && (
                            <Button type="primary" icon={<SaveOutlined />} loading={isLoading} onClick={() => handleSubmit("draft")}>
                                Lưu tạm
                            </Button>
                        )}
                        {!isHoanThanh && (
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                loading={isLoading}
                                disabled={fileList.length === 0}
                                onClick={() => handleSubmit("complete")}
                            >
                                Hoàn thành thanh lý
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>
        </Form>
    );
};

export default LuuThanhLyTaiSanBox;
